import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { Avatar } from '../../../layout/Avatar.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { useRfidScan } from '../../../shared/useRfidScan.js';
import { toast } from '../../../shared/useToast.js';

export function LookupBar({ onPick }) {
  const scanRef = useRef(null);
  const [scanUid, setScanUid] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [resolving, setResolving] = useState(false);

  useRfidScan({
    inputRef: scanRef,
    onScan: (uid) => {
      setScanUid(uid);
    },
    autoFocus: true,
    autoClear: false
  });

  // Resolve by uid (debounced)
  useEffect(() => {
    const uid = (scanUid || '').trim();
    if (uid.length < 4) return undefined;
    let cancelled = false;
    setResolving(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/sessions/by-uid/${encodeURIComponent(uid)}`);
        if (cancelled) return;
        const session = data?.session || data?.data || data;
        const id = session?._id || session?.id;
        if (id) {
          onPick?.(id);
          setScanUid('');
        } else {
          toast.warning('لا توجد جلسة نشطة لهذا الكارت');
        }
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 404) toast.warning('لا توجد جلسة نشطة لهذا الكارت');
        else toast.error('تعذّر التحقق من الكارت');
      } finally {
        if (!cancelled) setResolving(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [scanUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search results
  const searchQuery = useQuery({
    queryKey: ['checkout', 'search', searchQ],
    enabled: (searchQ || '').trim().length >= 2,
    queryFn: async () => {
      const { data } = await api.get('/sessions/search', { params: { q: searchQ } });
      return data?.sessions || data?.data || data || [];
    },
    staleTime: 4000
  });

  const recentQuery = useQuery({
    queryKey: ['checkout', 'recent-active'],
    queryFn: async () => {
      const { data } = await api.get('/sessions/active');
      return data?.sessions || data?.data || data || [];
    },
    staleTime: 10000
  });

  const recent = useMemo(() => {
    const list = Array.isArray(recentQuery.data) ? recentQuery.data : [];
    return [...list]
      .sort((a, b) => new Date(b.startTime || 0) - new Date(a.startTime || 0))
      .slice(0, 3);
  }, [recentQuery.data]);

  const results = Array.isArray(searchQuery.data) ? searchQuery.data : [];

  return (
    <div className="card p-4">
      <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">مسح كارت RFID</label>
          <div className="relative">
            <input
              ref={scanRef}
              className="input pr-9"
              placeholder="مرّر الكارت على القارئ..."
              value={scanUid}
              onChange={(e) => setScanUid(e.target.value)}
              dir="ltr"
              style={{ textAlign: 'right', fontFamily: 'monospace' }}
            />
            <Icon
              name="card"
              size={16}
              className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3 pointer-events-none"
            />
            {resolving ? (
              <div className="absolute top-1/2 -translate-y-1/2 left-3 text-[11px] text-ink-3">
                جاري التحقق...
              </div>
            ) : null}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">بحث بالاسم أو الهاتف</label>
          <div className="relative">
            <input
              className="input pr-9"
              placeholder="ابحث عن طفل أو ولي أمر..."
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
            />
            <Icon
              name="search"
              size={16}
              className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3 pointer-events-none"
            />
          </div>
        </div>
      </div>

      {(searchQ || '').trim().length >= 2 ? (
        <div className="mt-3 border border-line rounded-[10px] divide-y divide-line max-h-[260px] overflow-y-auto">
          {searchQuery.isLoading ? (
            <div className="p-3 text-center text-ink-3 text-sm">جاري البحث...</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-center text-ink-3 text-sm">لا توجد نتائج</div>
          ) : (
            results.map((s) => {
              const child = s.childId || s.child || {};
              const guardian = s.guardianId || s.guardian || {};
              return (
                <button
                  type="button"
                  key={s._id || s.id}
                  onClick={() => {
                    onPick?.(s._id || s.id);
                    setSearchQ('');
                  }}
                  className="w-full flex items-center gap-3 p-2.5 text-right hover:bg-paper-2 transition-colors"
                >
                  <Avatar name={child.name || '?'} id={child._id || child.id} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[13px] truncate">{child.name || '—'}</div>
                    <div className="text-[11px] text-ink-3 truncate">
                      {guardian.name || ''}{guardian.phone ? ` • ${guardian.phone}` : ''}
                    </div>
                  </div>
                  <span className="font-mono text-[11px] text-ink-3" dir="ltr">
                    {s.cardUid || s.card?.uid || ''}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}

      {recent.length > 0 ? (
        <div className="mt-3">
          <div className="text-[11px] text-ink-3 mb-1.5">آخر دخول:</div>
          <div className="flex flex-wrap gap-1.5">
            {recent.map((s) => {
              const child = s.childId || s.child || {};
              return (
                <button
                  type="button"
                  key={s._id || s.id}
                  onClick={() => onPick?.(s._id || s.id)}
                  className="pill bg-paper-2 hover:bg-orange-soft transition-colors"
                  style={{ borderWidth: 1, borderStyle: 'solid', borderColor: '#EFE7D6' }}
                >
                  <Avatar name={child.name || '?'} id={child._id || child.id} size="sm" className="!w-5 !h-5 !text-[10px]" />
                  <span>{child.name || '—'}</span>
                  {(s.pendingAmount || 0) > 0 || s.hasPendingInvoices ? (
                    <Pill variant="orange" className="!px-1.5 !py-0 !text-[10px]">معلقة</Pill>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
